import React, { useEffect, useState } from 'react';
import { Tag, X, CheckSquare, Square, Building2 } from 'lucide-react';
import { useOrders } from '../contexts/OrderContext';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { filterTodayOrders } from '../utils/dateFilters';
interface CompanyFilterSidebarProps {
  onCompanySelect?: (company: string) => void;
  selectedCompany?: string;
  // Multi-select support
  selectedCompanies?: Set<string>;
  onCompanyToggle?: (company: string) => void;
  multiSelect?: boolean;
}
export function CompanyFilterSidebar({
  onCompanySelect,
  selectedCompany,
  selectedCompanies,
  onCompanyToggle,
  multiSelect = false
}: CompanyFilterSidebarProps) {
  const {
    orders
  } = useOrders();
  const [companies, setCompanies] = useState<any[]>([]);
  // Load companies dynamically from Supabase or localStorage
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        if (isSupabaseConfigured()) {
          const data = await supabaseClient.getCompanies();
          setCompanies(data);
        } else {
          const saved = localStorage.getItem('criket_companies');
          setCompanies(saved ? JSON.parse(saved) : []);
        }
      } catch (error) {
        console.error('Error loading companies:', error);
        const saved = localStorage.getItem('criket_companies');
        setCompanies(saved ? JSON.parse(saved) : []);
      }
    };
    loadCompanies();
  }, []);
  // Use today's orders for stats
  const todayOrders = filterTodayOrders(orders);
  const getCompanyStats = (companyName: string) => {
    const companyOrders = todayOrders.filter(o => o.company === companyName && o.status !== 'Cancelado' && o.status !== 'Entregado');
    // Sum quantities instead of just counting orders
    const pending = companyOrders.filter(o => o.labelStatus === 'Pendiente').reduce((sum, o) => sum + (o.quantity || 1), 0);
    const total = companyOrders.reduce((sum, o) => sum + (o.quantity || 1), 0);
    return {
      pending,
      total
    };
  };
  // Show only companies that have active orders today (not all companies)
  const companiesWithOrders = companies.filter(company => {
    const stats = getCompanyStats(company.name);
    return stats.total > 0;
  });
  const handleClearFilters = () => {
    if (multiSelect && onCompanyToggle) {
      // Clear all by calling toggle for each selected
      selectedCompanies?.forEach(c => onCompanyToggle(c));
    } else if (onCompanySelect) {
      onCompanySelect('');
    }
  };
  const isCompanySelected = (name: string) => {
    if (multiSelect && selectedCompanies) {
      return selectedCompanies.has(name);
    }
    return selectedCompany === name;
  };
  const handleClick = (name: string) => {
    if (multiSelect && onCompanyToggle) {
      onCompanyToggle(name);
    } else if (onCompanySelect) {
      onCompanySelect(name);
    }
  };
  const selectedCount = multiSelect ? selectedCompanies?.size || 0 : selectedCompany ? 1 : 0;
  return <div className="h-full flex flex-col" data-id="element-196">
      <div className="flex justify-between items-center mb-6" data-id="element-197">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide flex items-center gap-2" data-id="element-198">
          <Building2 size={16} data-id="element-199" />
          Etiquetas por Empresa
        </h3>
      </div>

      <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-100 dark:border-gray-800" data-id="element-200">
        <button onClick={handleClearFilters} className="text-xs text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white cursor-pointer transition-colors font-medium hover:underline" data-id="element-201">
          Limpiar filtros
        </button>
        {selectedCount > 0 && <span className="text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded-full" data-id="element-202">
            {selectedCount} seleccionada{selectedCount !== 1 ? 's' : ''}
          </span>}
      </div>

      {multiSelect && companiesWithOrders.length > 1 && <button onClick={() => {
      if (selectedCompanies?.size === companiesWithOrders.length) {
        // Deselect all
        handleClearFilters();
      } else {
        // Select all
        companiesWithOrders.forEach(c => {
          if (!selectedCompanies?.has(c.name)) {
            onCompanyToggle?.(c.name);
          }
        });
      }
    }} className="w-full flex items-center gap-2 p-3 mb-3 rounded-xl text-xs font-bold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700" data-id="element-203">
          {selectedCompanies?.size === companiesWithOrders.length ? <CheckSquare size={16} className="text-blue-600" data-id="element-204" /> : <Square size={16} data-id="element-205" />}
          Seleccionar todas
        </button>}

      <div className="space-y-2 pr-2 overflow-y-auto flex-1" data-id="element-206">
        {companiesWithOrders.map(company => {
        const stats = getCompanyStats(company.name);
        const isSelected = isCompanySelected(company.name);
        return <div key={company.id} className={`flex items-start cursor-pointer group p-3 rounded-xl transition-all border ${isSelected ? 'bg-gray-900 border-gray-900 text-white shadow-md' : 'bg-white dark:bg-gray-900 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-200 dark:hover:border-gray-700'}`} onClick={() => handleClick(company.name)} data-id="element-207">
              <div className="flex items-center h-5 mt-0.5" data-id="element-208">
                {multiSelect ? isSelected ? <CheckSquare size={18} className="text-white" data-id="element-209" /> : <Square size={18} className="text-gray-400 group-hover:text-gray-600" data-id="element-210" /> : <input type="radio" name="company" checked={isSelected} onChange={() => {}} className="h-4 w-4 rounded-full border-gray-300 text-black focus:ring-black" data-id="element-211" />}
              </div>
              <div className="ml-3 flex-1 min-w-0" data-id="element-212">
                <div className="flex justify-between items-center mb-1" data-id="element-213">
                  <span className={`text-sm font-bold truncate pr-2 ${isSelected ? 'text-white' : 'text-gray-900 dark:text-white'}`} data-id="element-214">
                    {company.name}
                  </span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`} data-id="element-215">
                    {stats.total}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-216">
                  <span className={`text-xs ${isSelected ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-217">
                    {stats.pending} pendiente{stats.pending !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>;
      })}
        {companiesWithOrders.length === 0 && <div className="text-center p-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-218">
            <Tag className="w-8 h-8 mx-auto text-gray-300 mb-2" data-id="element-219" />
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-220">
              No hay pedidos activos hoy.
            </p>
            <p className="text-[10px] text-gray-400 mt-1" data-id="element-221">
              Las empresas aparecerán cuando tengan pedidos del día.
            </p>
          </div>}
      </div>
    </div>;
}